// ─── La scheda del socio: anagrafica, borsellino, attività ───────────────────
//
// 🎯 Qui sta l'altra metà del criterio della Fase 5: «si ricarica un borsellino
// e il saldo si muove subito; si preme due volte ricarica e il movimento resta
// UNO».

import { LucchettoNoShow } from './LucchettoNoShow.js';
import { useEffect, useRef, useState } from 'react';
import {
  ETICHETTE_MOVIMENTO_PREMIO,
  ETICHETTE_VERSO,
  fasceInParole,
  formatCents,
  livelloDaSalvare,
  livelloInParole,
  PAROLE_EVENTI,
  PAROLE_PARTITE,
  PAROLE_PROMEMORIA,
  PAROLE_RUOLO,
  preferenzeDelBot,
  parseCents,
  parolaDelLivello,
  sceltaDelLivello,
  segnoDelVerso,
  type MovimentoBorsellino,
  type SceltaDiLivello,
  type Socio,
} from '@pmo/shared';
import {
  usaArchiviazione,
  usaAttivita,
  usaBorsellino,
  usaModificaSocio,
  usaMovimentoBorsellino,
  usaPremio,
  usaScalaDeiLivelli,
  usaSocio,
  usaStornoMovimento,
} from '../../features/soci/dati.js';
import { etichettaTipo } from '../../features/calendario/Griglia.js';
import { useSessione } from '../../features/auth/sessione.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { InvitoBot } from './InvitoBot.js';
import { CampoTelefono } from './CampoTelefono.js';

export function SchedaSocio({ id, chiudi }: { id: string; chiudi: () => void }): React.JSX.Element {
  const foglio = useRef<HTMLDialogElement>(null);
  const socio = usaSocio(id);
  const { profilo } = useSessione();

  // `showModal()` e non l'attributo `open`: è la differenza fra un riquadro che
  // sembra un dialogo e un dialogo vero — sfondo, `Esc`, e il resto della
  // pagina che non si può più toccare per sbaglio.
  useEffect(() => {
    foglio.current?.showModal();
  }, []);

  const puòVedereBorsellino = profilo?.permissions.includes('view_members_borsellino') ?? false;
  const puòMuovereDenaro = profilo?.permissions.includes('manage_payments') ?? false;
  const puòModificare = profilo?.permissions.includes('view_members_anagrafica') ?? false;
  const puòVedereAttivita = profilo?.permissions.includes('view_members_attivita') ?? false;

  return (
    <dialog className="foglio scheda-socio" ref={foglio} onClose={chiudi}>
      {socio.isPending ? (
        <Attesa cosa="la scheda" />
      ) : socio.error ? (
        <Guasto errore={socio.error} />
      ) : socio.data ? (
        <>
          <header>
            <h2>{socio.data.displayName}</h2>
            <button type="button" className="piatto" onClick={() => foglio.current?.close()}>
              Chiudi
            </button>
          </header>

          {socio.data.archivedAt && (
            <p className="tenue piccolo">
              Archiviato il {new Date(socio.data.archivedAt).toLocaleDateString('it-IT')}. Resta tutto quello
              che ha fatto: un socio archiviato non è un socio cancellato.
            </p>
          )}

          <Anagrafica socio={socio.data} modificabile={puòModificare} />
          {/* 🤖 L'invito al bot dei soci (24/09/2026): sotto l'anagrafica, prima dei soldi. */}
          {puòModificare && (
            <InvitoBot id={id} nome={socio.data.firstName} nomeIntero={socio.data.displayName} />
          )}
          <DalBot socio={socio.data} />
          {/* 🔒 R16 (26/09/2026): il lucchetto dei no-show ripetuti, se c'è. */}
          <LucchettoNoShow id={id} puòTogliere={puòMuovereDenaro} />
          {puòVedereBorsellino && <Borsellino id={id} puòMuovere={puòMuovereDenaro} />}
          {puòVedereAttivita && <Attivita id={id} />}
          {puòModificare && <Archiviazione socio={socio.data} />}
        </>
      ) : null}
    </dialog>
  );
}

/**
 * 🤖 **Quello che il socio ha scelto sul bot** (25/09/2026, voci 12 e 13): quando
 * gioca di solito e cosa vuole ricevere. Lo sceglie lui, da «Area personale» o
 * parlando all'assistente; qui si legge e basta.
 *
 * ⚖️ «Non ha ancora scelto» si scrive così, e non è «no»: finché non sceglie,
 * le partite e i tornei non gli arrivano e i promemoria arrivano alle 13.
 * Senza nessuna scelta il riquadro non c'è: non c'è niente da leggere.
 */
function DalBot({ socio }: { socio: Socio }): React.JSX.Element | null {
  const p = preferenzeDelBot(socio.preferences);
  const n = p.notifications;
  if (!p.availability && !n.matches && !n.events && !n.reminders && !p.role) return null;
  const nonScelto = 'non ha ancora scelto';
  // 🆕 28/09/2026 (Partite Aperte, scelta 1A): per il bot il test è fatto con un
  // livello in scheda oppure con una prova consegnata. Stessa regola del ponte.
  const testFatto = socio.level !== null || socio.hasAssessment;
  return (
    <section className="riquadro">
      <h3>Scelto sul bot</h3>
      <dl className="piccolo">
        <dt>Nel bot</dt>
        <dd>{p.role ? PAROLE_RUOLO[p.role] : nonScelto}</dd>
        <dt>Test del livello</dt>
        <dd>{testFatto ? '✅ fatto' : '⏳ da fare: l’assistente glielo chiede a ogni gesto'}</dd>
        <dt>Gioca di solito</dt>
        <dd>
          {p.availability
            ? `giorni feriali: ${fasceInParole(p.availability.weekday)} · weekend: ${fasceInParole(p.availability.weekend)}`
            : nonScelto}
        </dd>
        <dt>Partite del suo livello</dt>
        <dd>{n.matches ? PAROLE_PARTITE[n.matches] : nonScelto}</dd>
        <dt>Tornei ed eventi</dt>
        <dd>{n.events ? PAROLE_EVENTI[n.events] : nonScelto}</dd>
        <dt>Promemoria dell'assistente</dt>
        <dd>{n.reminders ? PAROLE_PROMEMORIA[n.reminders] : 'sì, alle 13 (non ha ancora scelto)'}</dd>
      </dl>
    </section>
  );
}

/**
 * ⭐⭐ **L'IDENTIFICATIVO DEL SOCIO, in chiaro e copiabile** — 17/09/2026,
 * richiesta sua: *«Fammi vedere l'ID del socio nella scheda»*.
 *
 * 🔑 **È l'id NOSTRO**, quello che il database genera e che non cambia mai:
 * cambiando nome, telefono o email resta lo stesso, ed è con questo — non col
 * nome — che una persona è legata alle sue partite, ai suoi incassi e al suo
 * borsellino. È anche quello che si legge nell'indirizzo della pagina
 * (`/soci?socio=…`), quindi mostrarlo non svela niente che non fosse già lì.
 *
 * ⛔ **E NON è il codice con cui il socio è arrivato dal vecchio gestionale**
 * (`external_ref`): quello non esce da nessuna rotta, apposta. Il giorno in cui
 * una schermata lo mostrasse, qualcuno comincerebbe a usarlo per riconoscere
 * qualcuno, e l'identità sarebbe tornata a essere quella del sistema che stiamo
 * spegnendo.
 *
 * ⚖️ **Perché c'è il bottone e non solo il testo.** Un identificativo si legge
 * per riscriverlo altrove — in una segnalazione, in una ricerca, in un
 * messaggio. Trentasei caratteri ricopiati a mano si sbagliano, e un id
 * sbagliato di un carattere non dà errore: indica **un'altra persona** o
 * nessuna. ⇒ Il bottone c'è perché l'alternativa non è «copiare a mano», è
 * «copiare a mano un carattere storto».
 *
 * ⚠️ `navigator.clipboard` non c'è sempre (contesti non sicuri, permesso
 * negato): se non funziona **non si finge**. Il testo resta selezionabile, e
 * il bottone lo dice invece di lasciar credere che sia stato copiato.
 */
function Identificativo({ id }: { id: string }): React.JSX.Element {
  const [esito, setEsito] = useState<'fermo' | 'copiato' | 'nolo'>('fermo');

  useEffect(() => {
    if (esito === 'fermo') return;
    const t = setTimeout(() => setEsito('fermo'), 2500);
    return () => clearTimeout(t);
  }, [esito]);

  return (
    <span className="identificativo">
      <code>{id}</code>
      <button
        type="button"
        className="piatto"
        onClick={() => {
          void navigator.clipboard
            ?.writeText(id)
            .then(() => setEsito('copiato'))
            .catch(() => setEsito('nolo'));
          if (!navigator.clipboard) setEsito('nolo');
        }}
      >
        {esito === 'copiato' ? 'Copiato' : esito === 'nolo' ? 'Copialo a mano' : 'Copia'}
      </button>
    </span>
  );
}

// ── L'anagrafica ────────────────────────────────────────────────────────────

function Anagrafica({ socio, modificabile }: { socio: Socio; modificabile: boolean }): React.JSX.Element {
  const salva = usaModificaSocio(socio.id);
  const [aperto, setAperto] = useState(false);
  const [bozza, setBozza] = useState({
    firstName: socio.firstName,
    lastName: socio.lastName,
    nickname: socio.nickname ?? '',
    email: socio.email ?? '',
    phone: socio.phone ?? '',
    level: socio.level === null ? '' : String(socio.level),
    notes: socio.notes ?? '',
  });
  /**
   * 🎾 Il livello si sceglie a parole (25/09/2026), dalla scala del circolo.
   * `undefined` = non toccato: la tendina segna la fascia del livello di oggi,
   * calcolata quando la scala è arrivata. Senza scala resta il campo numerico
   * di prima — senza parole dichiarate non se ne inventano.
   */
  const fasce = usaScalaDeiLivelli().data?.fasce ?? [];
  const [sceltaToccata, setSceltaToccata] = useState<SceltaDiLivello | undefined>(undefined);
  const scelta = sceltaToccata === undefined ? sceltaDelLivello(fasce, socio.level) : sceltaToccata;
  const fuoriScala =
    fasce.length > 0 && socio.level !== null && parolaDelLivello(fasce, socio.level) === null;

  if (!aperto) {
    return (
      <section className="riquadro dati-socio">
        <dl>
          {/* 🏷️ Il soprannome (24/09/2026, dal mockup approvato): come lo chiama
              l'assistente su Telegram. Lo sceglie il socio dal bot; vuoto, lo
              chiama per nome — e la riga lo dice, perché «—» da solo non
              direbbe cosa succede. */}
          <dt>Soprannome</dt>
          <dd>
            {socio.nickname ?? (
              <>
                — <span className="tenue">(l’assistente lo chiama {socio.firstName})</span>
              </>
            )}
          </dd>
          <dt>Telefono</dt>
          <dd>{socio.phone ?? '—'}</dd>
          <dt>Email</dt>
          <dd>{socio.email ?? '—'}</dd>
          <dt>Livello</dt>
          <dd>
            {livelloInParole(fasce, socio.level)}
            {/* Un numero che la scala non chiama (il vecchio 0,5) si vede, in
                tenue: «Da definire» da solo nasconderebbe che un numero c'è. */}
            {fuoriScala && socio.level !== null && (
              <span className="tenue"> ({socio.level.toFixed(1).replace('.', ',')})</span>
            )}
          </dd>
          <dt>Note</dt>
          <dd>{socio.notes ?? '—'}</dd>
          <dt>ID</dt>
          <dd>
            <Identificativo id={socio.id} />
          </dd>
        </dl>
        {modificabile && (
          <button type="button" className="piatto" onClick={() => setAperto(true)}>
            Modifica l’anagrafica
          </button>
        )}
      </section>
    );
  }

  return (
    <form
      className="riquadro dati-socio"
      onSubmit={(e) => {
        e.preventDefault();
        salva.mutate(
          {
            firstName: bozza.firstName,
            lastName: bozza.lastName,
            nickname: bozza.nickname.trim() === '' ? null : bozza.nickname.trim(),
            // 🚨 Stringa vuota ⇒ `null`, cioè «svuotalo». Mandare `''` farebbe
            // scrivere un'email vuota che il vincolo di unicità considera un
            // valore: due soci «senza email» diventerebbero un doppione.
            email: bozza.email.trim() === '' ? null : bozza.email.trim(),
            phone: bozza.phone.trim() === '' ? null : bozza.phone.trim(),
            // 🚨 La stessa parola rimanda il numero di prima: un 3,5 resta 3,5.
            level:
              fasce.length > 0
                ? livelloDaSalvare(fasce, socio.level, scelta)
                : bozza.level.trim() === ''
                  ? null
                  : Number(bozza.level.replace(',', '.')),
            notes: bozza.notes.trim() === '' ? null : bozza.notes,
          },
          {
            onSuccess: () => {
              setSceltaToccata(undefined);
              setAperto(false);
            },
          },
        );
      }}
    >
      <Campo
        etichetta="Nome"
        required
        value={bozza.firstName}
        onChange={(e) => setBozza({ ...bozza, firstName: e.target.value })}
      />
      <Campo
        etichetta="Cognome"
        required
        value={bozza.lastName}
        onChange={(e) => setBozza({ ...bozza, lastName: e.target.value })}
      />
      <label className="campo">
        <span>Soprannome</span>
        <input
          value={bozza.nickname}
          maxLength={20}
          onChange={(e) => setBozza({ ...bozza, nickname: e.target.value })}
        />
        <small className="tenue">Come lo chiama l’assistente su Telegram. Vuoto: lo chiama per nome.</small>
      </label>
      <Campo
        etichetta="Email"
        type="email"
        value={bozza.email}
        onChange={(e) => setBozza({ ...bozza, email: e.target.value })}
      />
      <CampoTelefono
        etichetta="Telefono"
        value={bozza.phone}
        onChange={(phone) => {
          // Il rifiuto di prima parlava del numero vecchio: riscrivendolo, sparisce.
          salva.reset();
          setBozza({ ...bozza, phone });
        }}
      />
      {fasce.length > 0 ? (
        <label className="campo">
          <span>Livello</span>
          <select
            value={scelta === null ? '' : String(scelta)}
            onChange={(e) => {
              const v = e.target.value;
              setSceltaToccata(v === '' ? null : v === 'fuori' ? 'fuori' : Number(v));
            }}
          >
            <option value="">Da definire</option>
            {fuoriScala && socio.level !== null && (
              <option value="fuori">{socio.level.toFixed(1).replace('.', ',')} (fuori dalla scala)</option>
            )}
            {fasce.map((f, i) => (
              <option key={f.parola} value={String(i)}>
                {f.parola}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <Campo
          etichetta="Livello"
          inputMode="decimal"
          value={bozza.level}
          onChange={(e) => setBozza({ ...bozza, level: e.target.value })}
        />
      )}
      <label className="campo">
        <span>Note</span>
        <textarea
          rows={2}
          value={bozza.notes}
          onChange={(e) => setBozza({ ...bozza, notes: e.target.value })}
        />
      </label>
      <Guasto errore={salva.error} />
      <div className="azioni">
        <button type="submit" disabled={salva.isPending}>
          {salva.isPending ? 'Un attimo…' : 'Salva'}
        </button>
        <button
          type="button"
          className="bordato"
          onClick={() => {
            setSceltaToccata(undefined);
            setAperto(false);
          }}
        >
          Lascia stare
        </button>
      </div>
    </form>
  );
}

// ── Il borsellino ───────────────────────────────────────────────────────────

function Borsellino({ id, puòMuovere }: { id: string; puòMuovere: boolean }): React.JSX.Element {
  const borsellino = usaBorsellino(id);
  const storna = usaStornoMovimento(id);

  return (
    <section className="riquadro borsellino">
      <h3>Borsellino</h3>
      {borsellino.isPending ? (
        <Attesa cosa="il borsellino" />
      ) : (
        <>
          <p className={`saldo ${(borsellino.data?.balanceCents ?? 0) > 0 ? 'saldo-pieno' : 'tenue'}`}>
            {formatCents(borsellino.data?.balanceCents ?? 0)}
          </p>
          {/* 🌙 Il credito delle caparre: scade, e si usa per primo. */}
          {(borsellino.data?.credits ?? []).map((c) => (
            <p key={c.expiresOn} className="tenue piccolo">
              Di cui {formatCents(c.amountCents)} di Crediti dell’acconto · scadono il{' '}
              {c.expiresOn.split('-').reverse().join('/')}
            </p>
          ))}
          {(borsellino.data?.credits?.length ?? 0) > 0 && (
            <p className="tenue piccolo">
              I Crediti non si cambiano in denaro e non si passano ad altri soci. Si usano per pagare i campi.
            </p>
          )}
          {puòMuovere && <Movimento id={id} />}
          <Guasto errore={storna.error} />
          {(borsellino.data?.transactions.length ?? 0) === 0 ? (
            <p className="tenue piccolo">Nessun movimento.</p>
          ) : (
            <table className="movimenti">
              <tbody>
                {borsellino.data?.transactions.map((t) => (
                  <RigaMovimento
                    key={t.id}
                    m={t}
                    puòMuovere={puòMuovere}
                    storna={() => storna.mutate(t.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <CreditiPremio id={id} />
    </section>
  );
}

/**
 * 🎁 **I crediti premio** (voce 33, mockup approvato il 26/09/2026): stanno
 * accanto al saldo in euro ma NON sono euro. Qui non ci sono «Ricarica» né
 * «Togli»: non si prelevano, non si rimborsano, non si passano ad altri. Si
 * usano solo alla cassa, sulla quota di una partita.
 * ⚖️ Il riquadro non compare a chi non ne ha mai avuti, finché il premio è spento.
 */
function CreditiPremio({ id }: { id: string }): React.JSX.Element | null {
  const premio = usaPremio(id);
  const p = premio.data;
  if (!p || (!p.active && p.available === 0 && p.movements.length === 0)) return null;
  const inArrivo = p.incoming.reduce((s, x) => s + x.credits, 0);
  const crediti = (n: number) => `${n} ${Math.abs(n) === 1 ? 'Credito' : 'Crediti'}`;
  const data = (d: string) => d.split('-').reverse().join('/');
  return (
    <div className="crediti-premio">
      <h4>🎁 Crediti premio</h4>
      <p className="tenue piccolo">
        1 Credito = {formatCents(p.creditValueCents)} di sconto sulla quota di una partita.
      </p>
      <p className="saldo">{crediti(p.available)}</p>
      {inArrivo > 0 && (
        <p className="in-arrivo">
          ⏳ {crediti(inArrivo)} in arrivo, a fine partita:{' '}
          {p.incoming.map((x) => `${data(x.date)} ${x.time} · ${x.courtName}`).join(', ')}
        </p>
      )}
      {p.expiring.map((x) => (
        <p key={x.expiresOn} className="tenue piccolo">
          {crediti(x.credits)} scadono il {data(x.expiresOn)}
        </p>
      ))}
      <p className="tenue piccolo">
        Non si prelevano, non si rimborsano e non si passano ad altri soci: si usano alla cassa, quando lo
        chiede lui.
      </p>
      {p.movements.length > 0 && (
        <table className="movimenti">
          <tbody>
            {p.movements.map((m) => (
              <tr key={m.id}>
                <td>
                  {ETICHETTE_MOVIMENTO_PREMIO[m.kind]}
                  {m.booking && (
                    <span className="tenue piccolo">
                      {' '}
                      · {m.booking.replace(/^(\d{4})-(\d{2})-(\d{2})/, '$3/$2')}
                    </span>
                  )}
                  <br />
                  <span className="tenue piccolo">
                    {new Date(m.recordedAt).toLocaleString('it-IT', {
                      dateStyle: 'short',
                      timeStyle: 'short',
                    })}
                  </span>
                </td>
                <td className={`a-destra ${m.credits > 0 ? 'crediti-piu' : 'saldo-rosso'}`}>
                  {m.credits > 0 ? '+' : '−'}
                  {crediti(Math.abs(m.credits))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RigaMovimento({
  m,
  puòMuovere,
  storna,
}: {
  m: MovimentoBorsellino;
  puòMuovere: boolean;
  storna: () => void;
}): React.JSX.Element {
  const segno = segnoDelVerso(m.direction);
  /**
   * 🚨⭐⭐ **LO STORNO CHIEDE CONFERMA, e fino al 18/09/2026 partiva al primo
   * clic.**
   * ⚖️ Nella **cassa** lo stesso gesto ha sempre chiesto «Sì, storna»: stesso
   *   denaro, stessa irreversibilità (una riga di denaro non si cancella: se ne
   *   scrive una contraria), e due livelli di protezione diversi — con quello
   *   scoperto proprio dove il bottone sta **in ogni riga dell'elenco**, a un
   *   dito di distanza da «guardo il registro».
   * 📌 *Una protezione che c'è in un posto e non nell'altro non è una scelta:
   *    è il segno che i due posti sono stati scritti in due momenti diversi.*
   * ⭐ E la domanda dice **cosa** si sta per stornare — verso e cifra — perché
   *   in un elenco di righe che si somigliano la conferma senza il dettaglio
   *   protegge dal clic, non dallo sbaglio.
   */
  const [conferma, setConferma] = useState(false);
  /**
   * 🗣️ **«Fallo uguale nel borsellino»** (18/09/2026). In cassa una riga dice da
   * sé cos'è — `storno` o `stornato` — e il bottone non c'è dove non serve. Qui
   * il bottone c'era **su tutte**, e infatti si poteva stornare uno storno
   * all'infinito: misurato, tre righe da una rettifica sola.
   * ⚖️ Adesso il rifiuto arriva dal server (`STORNO_DI_UNO_STORNO`), ed è lui a
   *   decidere; questa riga toglie il bottone che porterebbe sempre a
   *   quell'errore. 📌 *Un bottone che porta sempre a un errore è peggio di un
   *   bottone che manca: il primo fa credere che il gesto esista.*
   */
  const eUnoStorno = m.reversesTransactionId !== null;
  const giaStornato = m.reversedByTransactionId !== null;
  return (
    <tr className={eUnoStorno ? 'storno' : giaStornato ? 'stornato' : undefined}>
      <td className="tenue piccolo">{new Date(m.recordedAt).toLocaleString('it-IT')}</td>
      <td>{ETICHETTE_VERSO[m.direction]}</td>
      <td className={segno > 0 ? 'saldo-pieno' : 'saldo-rosso'}>
        {segno > 0 ? '+' : '−'}
        {formatCents(m.amountCents)}
      </td>
      <td className="tenue piccolo">
        {m.reason ?? ''}
        {m.expiresOn && ` · scade il ${m.expiresOn.split('-').reverse().join('/')}`}
      </td>
      <td>
        {/* ⛔ Un movimento non si cancella: se ne scrive uno uguale e contrario.
            Lo rifiuterebbe anche il trigger del database. */}
        {eUnoStorno ? (
          <span className="tenue piccolo">storno</span>
        ) : giaStornato ? (
          <span className="tenue piccolo">stornato</span>
        ) : m.paymentId !== null ? (
          /**
           * 💶⭐ NASCE DA UNA RIGA DI CASSA (Fase 6): qui il bottone NON c'è, e
           * non è per prudenza — il server rifiuterebbe («si storna dalla
           * cassa, così tornano indietro tutt'e due»).
           * ⚖️ Un bottone che porta sempre a un errore è peggio di un bottone
           * che manca: il primo fa credere che il gesto esista e lascia a chi
           * lavora il compito di scoprire che no.
           */
          <span className="tenue piccolo">dalla cassa</span>
        ) : (
          puòMuovere &&
          (conferma ? (
            <span className="conferma-storno">
              <span className="tenue piccolo">
                Stornare {segno > 0 ? '+' : '−'}
                {formatCents(m.amountCents)}?
              </span>
              <button
                type="button"
                className="pericolo"
                onClick={() => {
                  setConferma(false);
                  storna();
                }}
              >
                Sì
              </button>
              <button type="button" className="piatto" onClick={() => setConferma(false)}>
                No
              </button>
            </span>
          ) : (
            <button type="button" className="piatto" onClick={() => setConferma(true)}>
              Storna
            </button>
          ))
        )}
      </td>
    </tr>
  );
}

function Movimento({ id }: { id: string }): React.JSX.Element {
  const [verso, setVerso] = useState<'recharge' | 'withdraw'>('recharge');
  const [importo, setImporto] = useState('');
  const [motivo, setMotivo] = useState('');
  /**
   * 💶⭐ CON CHE MEZZO IL DENARO PASSA DAL CASSETTO — ed è la riga che fa
   * quadrare la giornata (Fase 6). Una ricarica da 50,00 € in contanti mette
   * 50,00 € nel cassetto: senza questo, a fine serata la segreteria conta
   * cinquanta euro che il riepilogo della cassa non sa spiegare.
   * ⛔ «rettifica» è l'unica scelta che NON tocca la cassa, e si chiama così
   *    apposta: dev'essere una scelta, non una dimenticanza.
   */
  const [mezzo, setMezzo] = useState<'cash' | 'card' | ''>('cash');

  /**
   * 🚨 LA CHIAVE NASCE CON LA FINESTRA, NON COL CLIC — ed è la riga che vince
   * il criterio «si preme due volte e il movimento resta uno».
   *
   * ⚖️ Generandola al clic, due clic farebbero due chiavi e quindi due
   * movimenti. E il bottone disabilitato non basta: non ferma un `Invio`
   * ripetuto, non ferma una rete che rimanda la richiesta, e non ferma due
   * schede aperte sullo stesso socio. L'unica difesa che regge è che i due
   * tentativi portino la stessa chiave, e che a rifiutare il secondo sia il
   * database.
   * ⭐ Si rinnova dopo ogni movimento riuscito: la ricarica DOPO è un gesto
   *   nuovo, e deve poter passare.
   */
  const [chiave, setChiave] = useState(() => crypto.randomUUID());
  const muovi = usaMovimentoBorsellino(id, chiave);

  const cents = parseCents(importo);
  const valido = cents !== null && cents > 0;

  return (
    <form
      className="muovi-borsellino"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valido) return;
        muovi.mutate(
          {
            verso,
            amountCents: cents,
            reason: motivo.trim() === '' ? null : motivo.trim(),
            method: mezzo === '' ? null : mezzo,
          },
          {
            onSuccess: () => {
              setImporto('');
              setMotivo('');
              setChiave(crypto.randomUUID());
            },
          },
        );
      }}
    >
      {/*
       * ⭐ UNA PAROLA SOLA, e non è per brevità: il campo è largo ~9rem in una riga da quattro,
       * e «Togli dal borsellino» non ci sta — il <select> lo TRONCA invece di andare a capo,
       * quindi l'operatore leggeva «Togli dal bor…». Il riquadro si intitola già Borsellino:
       * «dal borsellino» ripeteva l'unica cosa che era già scritta sopra.
       */}
      <label className="campo">
        <span>Gesto</span>
        <select value={verso} onChange={(e) => setVerso(e.target.value as 'recharge' | 'withdraw')}>
          <option value="recharge">Carica</option>
          <option value="withdraw">Togli</option>
        </select>
      </label>
      <Campo
        etichetta="Importo (€)"
        inputMode="decimal"
        placeholder="10,00"
        value={importo}
        onChange={(e) => setImporto(e.target.value)}
      />
      {/*
       * ⚠️ Stessa misura, stesso difetto: «Rettifica (non tocca la cassa)» è
       * lungo 232px in una casella da 108 — l'operatore ne leggeva un terzo.
       * ⚖️ La spiegazione però NON si butta: è l'unica cosa che distingue una
       * correzione da un movimento di cassa vero. Va sotto, e compare solo
       * quando serve — cioè quando la rettifica è quella scelta.
       */}
      <label className="campo">
        <span>Mezzo</span>
        <select value={mezzo} onChange={(e) => setMezzo(e.target.value as 'cash' | 'card' | '')}>
          <option value="cash">Contanti</option>
          <option value="card">Carta</option>
          <option value="">Rettifica</option>
        </select>
      </label>
      <Campo etichetta="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      <Guasto errore={muovi.error} />
      <button type="submit" disabled={!valido || muovi.isPending}>
        {muovi.isPending ? 'Un attimo…' : verso === 'recharge' ? 'Carica' : 'Togli'}
      </button>
      {mezzo === '' && (
        <p className="tenue piccolo">La rettifica corregge il borsellino e non tocca la cassa.</p>
      )}
      {importo.trim() !== '' && !valido && <p className="guasto">Scrivi un importo, per esempio 10,00.</p>}
    </form>
  );
}

// ── L'attività ──────────────────────────────────────────────────────────────

function Attivita({ id }: { id: string }): React.JSX.Element {
  const attivita = usaAttivita(id);
  if (attivita.isPending) return <Attesa cosa="l’attività" />;
  const b = attivita.data?.bookings ?? [];
  const p = attivita.data?.payments ?? [];

  return (
    <section className="riquadro attivita">
      <h3>Attività</h3>
      {b.length === 0 ? (
        <p className="tenue piccolo">Non è mai stato in campo.</p>
      ) : (
        <table>
          <tbody>
            {b.slice(0, 10).map((r) => (
              <tr key={r.bookingId}>
                <td className="tenue piccolo">{r.date}</td>
                <td>{r.time}</td>
                <td>{r.courtName}</td>
                {/* 🔄 Una prenotazione si chiama col suo tipo, dal 17/09/2026
                    (⇒ `pezzi-scheda.tsx`): qui c'era `r.title ?? r.type`, che
                    avrebbe fatto riaffiorare i titoli vecchi proprio dove non
                    li cerca nessuno. */}
                <td>{etichettaTipo(r.type)}</td>
                <td>{r.amountDueCents === null ? '' : formatCents(r.amountDueCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {p.length > 0 && (
        <>
          <h4>Incassi</h4>
          <table>
            <tbody>
              {p.slice(0, 10).map((r) => (
                <tr key={r.id} className={r.status === 'voided' ? 'spento' : ''}>
                  <td className="tenue piccolo">{r.businessDate}</td>
                  <td>{r.method}</td>
                  <td>{formatCents(r.amountCents)}</td>
                  <td className="tenue piccolo">{r.status === 'voided' ? 'stornato' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

// ── Archiviare ──────────────────────────────────────────────────────────────

function Archiviazione({ socio }: { socio: Socio }): React.JSX.Element {
  const gesto = usaArchiviazione(socio.id);
  const [chiedo, setChiedo] = useState(false);
  const archiviato = socio.archivedAt !== null;

  if (archiviato) {
    return (
      <section className="riquadro">
        <Guasto errore={gesto.error} />
        <button
          type="button"
          className="piatto"
          disabled={gesto.isPending}
          onClick={() => gesto.mutate('restore')}
        >
          Rimetti fra i soci
        </button>
      </section>
    );
  }

  if (!chiedo) {
    return (
      <section className="riquadro">
        <button type="button" className="piatto" onClick={() => setChiedo(true)}>
          Archivia questo socio
        </button>
      </section>
    );
  }

  return (
    <section className="conferma">
      <p className="tenue piccolo">
        Archiviare <strong>{socio.displayName}</strong> lo toglie dagli elenchi e libera il suo telefono e la
        sua email. <strong>Non cancella niente</strong>: le partite, gli incassi e i movimenti restano dove
        sono. Si può rimettere dentro quando si vuole.
      </p>
      <Guasto errore={gesto.error} />
      <button type="button" disabled={gesto.isPending} onClick={() => gesto.mutate('archive')}>
        {gesto.isPending ? 'Un attimo…' : 'Sì, archivialo'}
      </button>
      <button type="button" className="piatto" onClick={() => setChiedo(false)}>
        Lascia stare
      </button>
    </section>
  );
}
